import React, { useState } from 'react';
import { BlogPost } from '../types';
import { Plus, X, Image as ImageIcon, Trash2, LayoutDashboard, Edit2 } from 'lucide-react';

interface AdminDashboardProps {
  posts: BlogPost[];
  onAddPost: (post: BlogPost) => void;
  onEditPost: (post: BlogPost) => void;
  onDeletePost: (id: string) => void;
  onLogout: () => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ posts, onAddPost, onEditPost, onDeletePost, onLogout }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [editingPostId, setEditingPostId] = useState<string | null>(null);
  
  // Form State
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    author: '',
    role: '',
    category: '',
    readTime: '',
    imageUrl: '',
    content: ''
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
        ...formData,
        [e.target.name]: e.target.value
    });
  };

  const handleEditClick = (post: BlogPost) => {
    setEditingPostId(post.id);
    setIsCreating(true);
    setFormData({
        title: post.title,
        excerpt: post.excerpt,
        author: post.author,
        role: post.role,
        category: post.category,
        readTime: post.readTime,
        imageUrl: post.imageUrl,
        content: typeof post.content === 'string' ? post.content : 'WARNING: This post uses custom formatting. Only edit if you want to replace everything with plain text.'
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingPostId) {
        const updatedPost: BlogPost = {
            id: editingPostId,
            date: posts.find(p => p.id === editingPostId)?.date || new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            ...formData
        };
        onEditPost(updatedPost);
    } else {
        const newPost: BlogPost = {
            id: Date.now().toString(),
            date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            ...formData
        };
        onAddPost(newPost);
    }
    
    setIsCreating(false);
    setEditingPostId(null);
    setFormData({
        title: '',
        excerpt: '',
        author: '',
        role: '',
        category: '',
        readTime: '',
        imageUrl: '',
        content: ''
    });
  };

  const handleCancel = () => {
      setIsCreating(false);
      setEditingPostId(null);
      setFormData({
          title: '',
          excerpt: '',
          author: '',
          role: '',
          category: '',
          readTime: '',
          imageUrl: '',
          content: ''
      });
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20 pt-20">
        <div className="bg-black text-white py-6 px-4 md:px-8 shadow-md">
            <div className="max-w-7xl mx-auto flex justify-between items-center">
                <div className="flex items-center gap-3">
                    <LayoutDashboard className="text-orange-500" />
                    <h1 className="text-xl font-bold">Admin Dashboard</h1>
                </div>
                <button onClick={onLogout} className="text-sm font-medium text-gray-400 hover:text-white transition-colors">
                    Logout
                </button>
            </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-8 mt-8">
            {!isCreating ? (
                <div>
                    <div className="flex justify-between items-center mb-8">
                        <h2 className="text-2xl font-bold text-gray-900">Manage Blog Posts</h2>
                        <button 
                            onClick={() => setIsCreating(true)}
                            className="bg-orange-500 hover:bg-orange-600 text-black px-6 py-2.5 rounded-lg font-bold flex items-center shadow-lg shadow-orange-500/20 transition-all"
                        >
                            <Plus className="w-5 h-5 mr-2" /> Write New Post
                        </button>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 border-b border-gray-100">
                                    <tr>
                                        <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Title</th>
                                        <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Author</th>
                                        <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Category</th>
                                        <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase">Date</th>
                                        <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {posts.map((post) => (
                                        <tr key={post.id} className="hover:bg-gray-50/50">
                                            <td className="px-6 py-4 font-medium text-gray-900 line-clamp-1 max-w-xs">{post.title}</td>
                                            <td className="px-6 py-4 text-gray-600">{post.author}</td>
                                            <td className="px-6 py-4">
                                                <span className="inline-block px-2 py-1 text-xs font-bold bg-orange-100 text-orange-700 rounded">
                                                    {post.category}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 text-gray-500 text-sm">{post.date}</td>
                                            <td className="px-6 py-4 text-right">
                                                <button 
                                                    onClick={() => handleEditClick(post)}
                                                    className="text-gray-400 hover:text-blue-500 transition-colors mr-2"
                                                    title="Edit Post"
                                                >
                                                    <Edit2 className="w-5 h-5" />
                                                </button>
                                                <button 
                                                    onClick={() => onDeletePost(post.id)}
                                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                                    title="Delete Post"
                                                >
                                                    <Trash2 className="w-5 h-5" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="max-w-3xl mx-auto">
                    <button 
                        onClick={handleCancel} 
                        className="mb-6 flex items-center text-gray-500 hover:text-black font-medium"
                    >
                        <X className="w-4 h-4 mr-2" /> Cancel
                    </button>
                    
                    <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">
                            {editingPostId ? 'Edit Article' : 'Create New Article'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700">Title</label>
                                    <input 
                                        name="title" 
                                        required 
                                        value={formData.title} 
                                        onChange={handleChange} 
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500" 
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700">Category</label>
                                    <input 
                                        name="category" 
                                        required 
                                        placeholder="e.g. Strategy, Compliance"
                                        value={formData.category} 
                                        onChange={handleChange} 
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500" 
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <label className="text-sm font-bold text-gray-700">Excerpt (Short Summary)</label>
                                <textarea 
                                    name="excerpt" 
                                    required 
                                    rows={2}
                                    value={formData.excerpt} 
                                    onChange={handleChange} 
                                    className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500" 
                                />
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700">Author Name</label>
                                    <input 
                                        name="author" 
                                        required 
                                        value={formData.author} 
                                        onChange={handleChange} 
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500" 
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700">Author Role</label>
                                    <input 
                                        name="role" 
                                        required 
                                        placeholder="e.g. Head of Growth"
                                        value={formData.role} 
                                        onChange={handleChange} 
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500" 
                                    />
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700">Image URL</label>
                                    <div className="relative">
                                        <ImageIcon className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
                                        <input 
                                            name="imageUrl" 
                                            required 
                                            placeholder="https://..."
                                            value={formData.imageUrl} 
                                            onChange={handleChange} 
                                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500" 
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-bold text-gray-700">Read Time</label>
                                    <input 
                                        name="readTime" 
                                        required 
                                        placeholder="e.g. 5 min read"
                                        value={formData.readTime} 
                                        onChange={handleChange} 
                                        className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500" 
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-bold text-gray-700">Content</label>
                                <p className="text-xs text-gray-500 mb-2">Separate your paragraphs with a double line break.</p>
                                <textarea 
                                    name="content" 
                                    required 
                                    rows={10}
                                    placeholder="Write your article here..."
                                    value={formData.content} 
                                    onChange={handleChange} 
                                    className="w-full px-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500" 
                                />
                            </div>

                            <div className="pt-4 flex justify-end">
                                <button type="submit" className="bg-black text-white px-8 py-3 rounded-xl font-bold hover:bg-gray-800 transition-all shadow-lg">
                                    {editingPostId ? 'Save Changes' : 'Publish Article'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    </div>
  );
};

export default AdminDashboard;